import { catchError, filter, from, map, of, switchMap, throwError } from 'rxjs';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { Injectable, inject } from '@angular/core';
import {
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { InteractionStatus } from '@azure/msal-browser';
import { OAuthService } from 'angular-oauth2-oidc';
import { environment } from '@et/environment';
import {
  TokenRefreshCoordinator,
  TokenRefreshUnavailableError,
} from '../token-refresh/token-refresh.coordinator';

/**
 * Single guard for both auth backends. Flag flips at module-eval time via
 * `environment.useBetterAuth`:
 *  - `true`  → check access or id token (Better Auth / OIDC PKCE; SPA sends id_token);
 *  - `false` → fall through to the existing MSAL/B2C check.
 *
 * Either branch redirects unauthenticated requests to `/auth/login` with `returnUrl`,
 * which `LoginComponent` then routes to the right backend.
 */
@Injectable({ providedIn: 'root' })
export class JwtGuard {
  private router = inject(Router);
  // Better Auth path may run without MSAL/OAuth providers, so keep these optional
  // (the checkMsal/checkBetterAuth bodies null-guard them).
  private msalService = inject(MsalService, { optional: true });
  private msalBroadcastService = inject(MsalBroadcastService, {
    optional: true,
  });
  private oauthService = inject(OAuthService, { optional: true });
  private tokenRefreshCoordinator = inject(TokenRefreshCoordinator, {
    optional: true,
  });

  canActivate(_: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    if (environment.useBetterAuth) {
      return this.checkBetterAuth(state);
    }
    return this.checkMsal(state);
  }

  /**
   * A token that expired while the tab was inactive is still recoverable via the
   * refresh token, so try that once before bouncing to `/auth/login` — otherwise a
   * navigation landing right on the expiry boundary would force a needless re-login.
   */
  private checkBetterAuth(state: RouterStateSnapshot) {
    if (
      this.oauthService?.hasValidAccessToken() ||
      this.oauthService?.hasValidIdToken()
    ) {
      return of(true);
    }
    return from(
      this.tokenRefreshCoordinator?.ensureValidToken() ??
        Promise.resolve(false),
    ).pipe(
      map((refreshed) => {
        if (refreshed) {
          return true;
        }
        this.router.navigate(['/auth/login'], {
          queryParams: { returnUrl: state.url },
        });
        return false;
      }),
      // Issuer unreachable (offline/5xx): the session is intact, so don't bounce to
      // login — let the route load; the bearer interceptor retries once back online.
      catchError((error: unknown) =>
        error instanceof TokenRefreshUnavailableError
          ? of(true)
          : throwError(() => error),
      ),
    );
  }

  private checkMsal(state: RouterStateSnapshot) {
    if (!this.msalService || !this.msalBroadcastService) {
      this.router.navigate(['/auth/login'], {
        queryParams: { returnUrl: state.url },
      });
      return of(false);
    }
    const msal = this.msalService;
    return this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      switchMap(() => {
        if (msal.instance.getAllAccounts().length > 0) {
          return of(true);
        }
        this.router.navigate(['/auth/login'], {
          queryParams: { returnUrl: state.url },
        });
        return of(false);
      }),
    );
  }
}
